import { Suspense, lazy } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { screenByBoard } from '@screens';
import { hasDesign } from './boards';

const BoardView = lazy(() => import('./BoardView'));

const loading = (
  <div className="cv" style={{ padding: 28 }} aria-busy="true">
    Loading design…
  </div>
);

/**
 * Working pages keep their real page as the default. Each board the page implements gets a small
 * "View 1:1 design" link; `?view=design` (and `&board=<stem>` when several boards share the path)
 * swaps in the board's 1:1 design preview.
 */
export function DesignSwitch({ boards, children }: { boards: string[]; children: ReactNode }) {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const list = boards.filter(hasDesign);
  if (!list.length) return <>{children}</>;
  if (params.get('view') === 'design') {
    const wanted = params.get('board');
    const board = wanted && list.includes(wanted) ? wanted : list[0];
    return (
      <Suspense fallback={loading}>
        <BoardView key={board} board={board} workingPath={pathname} />
      </Suspense>
    );
  }
  return (
    <>
      <div className="cv-design-links" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 6, padding: '6px 16px 0' }}>
        {list.map((b) => (
          <Link
            key={b}
            to={`${pathname}?view=design${list.length > 1 ? `&board=${encodeURIComponent(b)}` : ''}`}
            className="cv-btn cv-btn-s"
            style={{ minHeight: 28, fontSize: 12 }}
            data-testid="view-design-link"
          >
            View 1:1 design{list.length > 1 ? ` · ${screenByBoard(b)?.id ?? b}` : ''}
          </Link>
        ))}
      </div>
      {children}
    </>
  );
}

/** /design/:board renders any compiled board (phone boards and boards without their own route too). */
export function DesignBoardPage() {
  const { board = '' } = useParams();
  if (!hasDesign(board)) {
    return (
      <div className="cv" style={{ padding: 28 }} role="status">
        No canvas board is named <code>{board}</code>.
      </div>
    );
  }
  const s = screenByBoard(board);
  const working = s?.wiredWeb ? s.wiredWeb.replace(/\/:[^/]+/g, '') || '/' : null;
  return (
    <Suspense fallback={loading}>
      <BoardView key={board} board={board} workingPath={working} />
    </Suspense>
  );
}
